<script setup lang="ts">
import { LMap, LTileLayer, LIcon, LPopup, LMarker, LControl } from '@vue-leaflet/vue-leaflet'
const scroll = ref(false)
const { locale } = useI18n()

function enableScroll() {
	scroll.value = true
}

const map = ref<any>(null)
const localePath = useLocalePath()
const router = useRouter()
const locationsStore = useLocationsStore()
const route = useRoute()
const reservationStore = useReservationStore()
const items = ref<ILocationModel[] | undefined>(undefined)
const bookerStore = useBookerStore()
const bookerState = ref<IBookerState>(bookerStore.getBooker())
const userLocationMarker = ref(null)
const L = ref<any>(null)
const { isMobile_lg } = useWindowWidth()
const notyf = useNotyfService()
const $t = useI18n().t

const props = defineProps<{
	contact?: boolean
}>()

const initLeaflet = async () => {
	if (!L.value) {
		const leaflet = await import('leaflet')
		L.value = leaflet.default
	}
	return L.value
}

// const getLocations = async () => {
// 	try {
// 		const locations = reservationStore.getAllLocations
// 		if (locations && locations.length > 0) {
// 			items.value = locations
// 			return
// 		}
// 		const { data } = await ApiService.fetchAllLocations(1, 100, undefined, true)
// 		items.value = data

// 		// for (const item of items.value) {
// 		// 	// check if item has crm_id if not delete
// 		// 	if (!item?.crmID) {
// 		// 		const index = items.value?.indexOf(item)
// 		// 		items.value?.splice(index, 1)
// 		// 	}
// 		// }

// 		reservationStore.updateAllLocations(data)
// 	} catch (error) {
// 		console.log(error)
// 	}
// }
onMounted(async () => {
	items.value = await locationsStore.fetchLocations()
})

watch(
	() => reservationStore.getAllLocations,
	(newValue) => {
		if (newValue) {
			items.value = newValue

			// for (const item of items.value) {
			// 	// check if item has crm_id if not delete
			// 	if (!item?.crmID) {
			// 		const index = items.value?.indexOf(item)
			// 		items.value?.splice(index, 1)
			// 	}
			// }
		}
	},
	{
		immediate: true,
		deep: true,
	},
)

watch(
	() => scroll,
	() => {
		if (scroll.value === true) {
			map.value?.leafletObject.scrollWheelZoom.enable()
		}
	},
)

watch(isMobile_lg, () => {
	if (isMobile_lg.value) {
		map.value?.leafletObject.dragging.disable()
		map.value?.leafletObject.tapHold.disable()
	} else {
		map.value?.leafletObject.dragging.enable()
		map.value?.leafletObject.tapHold.enable()
	}
})

const onMapReady = async () => {
	await initLeaflet()
	setTimeout(() => {
		if (map.value?.leafletObject) {
			map.value.leafletObject.invalidateSize()
			if (isMobile_lg.value) {
				map.value.leafletObject.dragging.disable()
				map.value.leafletObject.tapHold.disable()
			} else {
				map.value.leafletObject.dragging.enable()
				map.value.leafletObject.tapHold.enable()
			}
		}
	}, 100)
}

const data = computed(() => {
	if (props.contact) {
		return items.value
			?.filter((i) => i.purpose !== 'meet_and_greet')
			.map((i) => ({
				id: i.id,
				lng: i.geo_point?.coordinates[0] ?? 53,
				lat: i.geo_point?.coordinates[1] ?? 24,
				airport: i.icon === 'airplane',
				meetAndGreet: i.purpose === 'meet_and_greet',
				phoneNumber: i.phone_number ?? '+48794500550',
				name: locale.value === 'pl' ? i.name : i.nameEn,
				street: `${i.address_street} ${i.address_house_number}`,
			}))
	}

	return items.value?.map((i) => ({
		id: i.id,
		lng: i.geo_point?.coordinates[0] ?? 53,
		lat: i.geo_point?.coordinates[1] ?? 24,
		airport: i.icon === 'airplane',
		meetAndGreet: i.purpose === 'meet_and_greet',
		phoneNumber: i.phone_number ?? '+48794500550',
		name: locale.value === 'pl' ? i.name : i.nameEn,
		street: `${i.address_street} ${i.address_house_number}`,
	}))
})

const handleElementClick = (id: number) => {
	const item = items.value?.find((i) => i.id === id)

	if (item) {
		bookerState.value.selectedLocations[EBookerLocationType.START] = item
		reservationStore.setBookerLocations({ ...bookerState.value.selectedLocations, end: null })
		reservationStore.addLocationToHistory(item.id)
		if (route.name?.toString().slice(0, -5) === 'locations') router.push(localePath('cars'))
	}
}

const mapOptions = computed(() => ({
	zoomControl: true,
	scrollWheelZoom: false,
	fullscreenControl: true,
}))

const maximizeMap = () => {
	try {
		if (map.value?.leafletObject) {
			if (document.fullscreenElement) {
				document.exitFullscreen()
			} else {
				map.value.leafletObject.getContainer().requestFullscreen()
			}
		}
	} catch (error) {
		notyf?.error((error as Error).message)
	}
}

const getCurrentLocation = async () => {
	try {
		const L = await initLeaflet()

		if ('geolocation' in navigator) {
			navigator.geolocation.getCurrentPosition(
				(position) => {
					const { latitude, longitude } = position.coords
					map.value?.leafletObject.setView([latitude, longitude], 13)

					// Add a marker for the user's location
					const userIcon = L.icon({
						iconUrl:
							"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='320' height='512' viewBox='0 0 320 512'%3E%3Cpath fill='%23ff5c00' d='M112 48a48 48 0 1 1 96 0a48 48 0 1 1-96 0m40 304v128c0 17.7-14.3 32-32 32s-32-14.3-32-32V256.9l-28.6 47.6c-9.1 15.1-28.8 20-43.9 10.9s-20-28.8-10.9-43.9l58.3-97c17.4-28.9 48.6-46.6 82.3-46.6h29.7c33.7 0 64.9 17.7 82.3 46.6l58.3 97c9.1 15.1 4.2 34.8-10.9 43.9s-34.8 4.2-43.9-10.9L232 256.9V480c0 17.7-14.3 32-32 32s-32-14.3-32-32V352z'/%3E%3C/svg%3E",
						iconSize: [25, 41],
						iconAnchor: [12, 41],
						popupAnchor: [1, -34],
					})

					if (userLocationMarker.value) {
						map.value?.leafletObject.removeLayer(userLocationMarker.value)
					}

					userLocationMarker.value = L.marker([latitude, longitude], { icon: userIcon }).addTo(map.value.leafletObject)
				},
				(error) => {
					console.error('Error getting location:', error.message)
					notyf?.error($t('error.unable_to_retrieve'))
				},
			)
		} else {
			notyf?.error($t('error.geolocation_is_not_supported'))
		}
	} catch (error) {
		console.error('Error initializing Leaflet:', error)
	}
}

onMounted(async () => {
	await initLeaflet()

	if (map.value?.leafletObject) {
		map.value.leafletObject.on('fullscreenchange', () => {
			map.value?.leafletObject.invalidateSize()
		})
	}
})

const formatPhoneNumber = (phoneNumber: string) => {
	if (!phoneNumber || phoneNumber?.length < 9) return phoneNumber

	const phone = phoneNumber.replace('+', '')
	return `+${phone.slice(0, 2)} ${phone.slice(2, 5)} ${phone.slice(5, 8)} ${phone.slice(8, 11)}`.trim()
}
</script>

<template>
	<section class="full-map" @click="enableScroll">
		<h2 data-aos="fade-up" class="full-map__title">{{ $t('components.departments.full_map.title') }}</h2>
		<div data-aos="fade-up" class="full-map__map">
			<ClientOnly>
				<LMap
					ref="map"
					:zoom="11"
					:center="[52.21564168678129, 21.005791780852896]"
					:use-global-leaflet="false"
					:options="mapOptions"
					@ready="onMapReady">
					<LTileLayer
						url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
						attribution='&amp;copy; <a href="https://www.openstreetmap.org/">OpenStreetMap</a> contributors'
						layer-type="base"
						name="OpenStreetMap" />
					<template v-for="(item, index) in data" :key="index">
						<LMarker :lat-lng="[item.lat, item.lng]">
							<LIcon :icon-size="[20, 20]">
								<GlobalIcon v-if="item.airport && !item.meetAndGreet" class="full-map__icon" name="airplane" :width="20" />
								<GlobalIcon
									v-else-if="!item.airport && !item.meetAndGreet && !contact"
									class="full-map__icon"
									name="location-filled"
									:width="20" />
								<GlobalIcon v-else-if="item.meetAndGreet || contact" class="full-map__icon" name="location-bold" :width="20" />
							</LIcon>
							<LPopup :options="{ direction: 'top', offset: [0, -20], closeButton: false }">
								<div class="full-map__tooltip">
									<GlobalIcon v-if="item.airport && !contact" class="full-map__icon" name="airplane" :width="20" />
									<GlobalIcon v-if="!item.airport && !contact" class="full-map__icon" name="location-bold" :width="20" />
									<div class="full-map__tooltip-text-container">
										<p class="full-map__tooltip-text full-map__tooltip-text--title">
											{{ item.name }}
										</p>
										<p v-if="!item.street?.includes('null')" class="full-map__tooltip-text full-map__tooltip-text--street">
											{{ item.street }}
										</p>
										<div class="full-map__tooltip-text full-map__tooltip-text--number">
											<GlobalIcon name="call" :height="14" class="header__call-btn__icon" />
											<p>
												{{
													!item.phoneNumber?.includes('null') && item?.phoneNumber
														? formatPhoneNumber(item.phoneNumber)
														: '+48 794 500 550'
												}}
											</p>
										</div>
										<NuxtLink
											v-if="!contact"
											:to="undefined"
											class="full-map__tooltip-text full-map__tooltip-text--link"
											@click="handleElementClick(item.id)"
											>{{ $t('components.departments.full_map.tooltip_link') }}</NuxtLink
										>
									</div>
								</div>
							</LPopup>
						</LMarker>
					</template>
					<LControl position="bottomright">
						<div class="full-map__btn-container">
							<button class="full-map__btn full-map__btn--maximize" @click="maximizeMap">
								<GlobalIcon name="maximize" :height="20" />
							</button>
							<button class="full-map__btn full-map__btn--gps" aria-label="Get current location" @click="getCurrentLocation">
								<GlobalIcon name="gps" :height="20" />
							</button>
						</div>
					</LControl>
				</LMap>
			</ClientOnly>
		</div>
	</section>
</template>

<style scoped lang="scss">
@import './FullMap.scss';
</style>
